import { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";

import { api } from "./api";
import type { Health } from "./types";
import { Chip, Icon } from "./ui";

/**
 * The shell.
 *
 * The bar carries one thing that is not navigation: which provider read
 * these documents. A screen full of extracted invoices with nothing
 * saying they came from a heuristic is how a demonstration quietly
 * becomes a claim, and the person who needs to know is whoever is
 * looking at the numbers.
 */
/**
 * What a visitor needs to know before they upload anything.
 *
 * This demo has no accounts, so it cannot give anyone a private queue.
 * What it does instead is tell the truth and hand them a delete button,
 * and that only works if the sentence is met before the upload rather
 * than found afterwards. So it sits above the content, on every page,
 * and it is not dismissible: a notice you can close is a notice the
 * next person does not read.
 */
function SharedDemoNotice() {
  return (
    <div className="demo-notice" role="note">
      <strong>This is a shared demo.</strong> Anything you upload is visible to
      everyone else who opens this page, and it is read by an AI model. It stays
      until you delete it, and there is a delete button on every document.
      Please do not upload anything confidential.
    </div>
  );
}

/**
 * What the rest of the app can ask the shell to do.
 *
 * The document count in the bar and the count in the queue are the same
 * fact fetched twice, and the bar used to fetch it once on mount and
 * never again. Upload two documents and the header said 40 while the
 * table said 42 - two numbers for one fact, disagreeing, on a screen
 * whose entire argument is that its numbers can be trusted.
 *
 * So whoever changes the set of documents says so, and the bar refetches.
 */
export interface Shell {
  documentsChanged: () => void;
}

export function App() {
  const [health, setHealth] = useState<Health | null>(null);

  const refresh = useCallback(() => {
    api.health().then(setHealth).catch(() => setHealth(null));
  }, []);

  useEffect(() => {
    refresh();

    // This is a shared demo: somebody else's upload changes the count
    // without this tab doing anything. Refetching when the tab is looked
    // at again is the cheap version of noticing that, and it costs one
    // request at the moment a stale number would otherwise be read.
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", refresh);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", refresh);
    };
  }, [refresh]);

  return (
    <>
      <header className="topbar">
        <div className="brand">
          {Icon.logo({ size: 24 })}
          <div>
            Invoice extraction
            <small>reliability console</small>
          </div>
        </div>

        <nav className="tabs">
          <NavLink to="/" end>
            Queue
          </NavLink>
          <NavLink to="/analytics">Analytics</NavLink>
        </nav>

        <div className="spacer" />

        {health && (
          <>
            <span className="note" style={{ whiteSpace: "nowrap" }}>
              {health.documents} documents
            </span>
            <Chip
              tone={health.simulated ? "warn" : "ok"}
              icon={health.simulated ? Icon.alert({ size: 13 }) : Icon.check({ size: 13 })}
            >
              {health.simulated ? "Simulated reader" : "Live model"} · {health.model}
            </Chip>
          </>
        )}
      </header>

      <SharedDemoNotice />

      <Outlet context={{ documentsChanged: refresh } satisfies Shell} />
    </>
  );
}
